import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { mutate } from 'swr';
import { useAdminLocale } from './admin-locale.js';
import { buildApiUrl } from './api.js';
import { useRemote } from './remote.js';

export const DEFAULT_TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/** @deprecated use DEFAULT_TABLE_PAGE_SIZE_OPTIONS — kept for existing imports */
export const TABLE_PAGE_SIZE_OPTIONS = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;

export const DEFAULT_PRODUCT_NAME = 'UNIBYTE AI';
export const DEFAULT_ACCENT_COLOR = '#2563EB';
export const DEFAULT_SIDEBAR_BG = '#0B1220';
export const DEFAULT_SIDEBAR_TEXT = '#94A3B8';
export const DEFAULT_SIDEBAR_ACTIVE_BG = '#2563EB';
export const DEFAULT_SIDEBAR_ACTIVE_TEXT = '#FFFFFF';
/** Signal Ink: light mode also uses ink chrome */
export const DEFAULT_SIDEBAR_BG_LIGHT = '#0B1220';
export const DEFAULT_SIDEBAR_TEXT_LIGHT = '#94A3B8';
export const DEFAULT_SIDEBAR_ACTIVE_BG_LIGHT = '#2563EB';
export const DEFAULT_SIDEBAR_ACTIVE_TEXT_LIGHT = '#FFFFFF';
export const DEFAULT_SELECTION_BG = '#2563EB';
export const DEFAULT_SELECTION_TEXT = '#FFFFFF';
export const DEFAULT_TEXT_COLOR = '#0F172A';
export const DEFAULT_TEXT_COLOR_DARK = '#E2E8F0';
export const DEFAULT_FONT_FAMILY = 'ibm_plex';

export const ADMIN_FONT_STACKS: Record<string, string> = {
  ibm_plex: "'IBM Plex Sans', 'Segoe UI', ui-sans-serif, system-ui, sans-serif",
  inter: "'Inter', 'Segoe UI', ui-sans-serif, system-ui, sans-serif",
  system: "ui-sans-serif, system-ui, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'IBM Plex Mono', ui-monospace, 'Cascadia Code', monospace",
};
export const DEFAULT_LOGIN_SUBTITLE =
  'Πίνακας διαχείρισης του AI chatbot: γνώση, συνομιλίες, εγκαταστάσεις WordPress και όρια χρήσης. Η πρόσβαση δίνεται μόνο με πρόσκληση.';
export const DEFAULT_AUTH_HEADLINE = 'Διαχειριστείτε το AI chatbot των ιστοσελίδων σας.';
export const DEFAULT_AUTH_BODY =
  'Συγχρονισμός WordPress, τρίγλωσσο widget (el/en/fr), απαντήσεις μόνο από εγκεκριμένη γνώση με παραπομπές, απομόνωση ανά πελάτη και μετρημένες κλήσεις AI.';
const DEFAULT_LOGIN_SUBTITLE_EN =
  'AI chatbot admin panel: knowledge, conversations, WordPress installations, and usage limits. Access is invitation-only.';
const DEFAULT_AUTH_HEADLINE_EN = 'Manage the AI chatbot for your websites.';
const DEFAULT_AUTH_BODY_EN =
  'WordPress synchronization, a trilingual widget (el/en/fr), answers only from approved knowledge with citations, customer isolation, and metered AI calls.';
export const DEFAULT_PLUGIN_MENU_NAME = 'uni-Chatbot.AI';
export const DEFAULT_PLUGIN_PAGE_TITLE = 'Unibyte Chatbot.AI';

function hexRgb(value: string): [number, number, number] | null {
  const normalized = value.trim().replace(/^#/, '');
  if (!/^[\da-f]{6}$/i.test(normalized)) return null;
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function relativeLuminance(rgb: [number, number, number]): number {
  return rgb.reduce((total, channel, index) => {
    const linear =
      channel / 255 <= 0.03928 ? channel / 255 / 12.92 : ((channel / 255 + 0.055) / 1.055) ** 2.4;
    return total + linear * [0.2126, 0.7152, 0.0722][index]!;
  }, 0);
}

function contrastRatio(first: string, second: string): number {
  const firstRgb = hexRgb(first);
  const secondRgb = hexRgb(second);
  if (!firstRgb || !secondRgb) return 1;

  const firstLuminance = relativeLuminance(firstRgb);
  const secondLuminance = relativeLuminance(secondRgb);
  return (
    (Math.max(firstLuminance, secondLuminance) + 0.05) /
    (Math.min(firstLuminance, secondLuminance) + 0.05)
  );
}

function readableSidebarColors(
  sidebarBg: string,
  activeBg: string,
  activeText: string,
  accent: string,
): { background: string; text: string } {
  const background = contrastRatio(sidebarBg, activeBg) >= 1.35 ? activeBg : accent;
  const text =
    contrastRatio(background, activeText) >= 3
      ? activeText
      : contrastRatio(background, '#0f172a') >= 3
        ? '#0f172a'
        : '#ffffff';
  return { background, text };
}

export function parseTablePageSizeOptions(raw: string | undefined): number[] {
  if (!raw?.trim()) return [...DEFAULT_TABLE_PAGE_SIZE_OPTIONS];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [...DEFAULT_TABLE_PAGE_SIZE_OPTIONS];
    const sizes = parsed
      .map((entry) => (typeof entry === 'number' ? entry : Number.parseInt(String(entry), 10)))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 1000);
    return sizes.length > 0
      ? [...new Set(sizes)].sort((a, b) => a - b)
      : [...DEFAULT_TABLE_PAGE_SIZE_OPTIONS];
  } catch {
    return [...DEFAULT_TABLE_PAGE_SIZE_OPTIONS];
  }
}

export function normalizeTablePageSize(value: number, options?: readonly number[]): number {
  const allowed = options && options.length > 0 ? options : DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  return allowed.includes(value) ? value : (allowed[0] ?? 20);
}

interface UiPrefs {
  productName: string;
  loginSubtitle: string;
  authHeadline: string;
  authBody: string;
  accentColor: string;
  sidebarBg: string;
  sidebarText: string;
  sidebarActiveBg: string;
  sidebarActiveText: string;
  sidebarBgLight: string;
  sidebarTextLight: string;
  sidebarActiveBgLight: string;
  sidebarActiveTextLight: string;
  selectionBg: string;
  selectionText: string;
  textColor: string;
  textColorDark: string;
  fontFamily: string;
  logoUrl: string;
  faviconUrl: string;
  logoUploaded: boolean;
  faviconUploaded: boolean;
  pluginMenuName: string;
  pluginPageTitle: string;
  tablePageSize: number;
  tablePageSizeOptions: number[];
  retentionDayChoices: number[];
  ready: boolean;
}

const DEFAULT_RETENTION_DAYS = [30, 90, 180, 365];

const UiPrefsContext = createContext<UiPrefs>({
  productName: DEFAULT_PRODUCT_NAME,
  loginSubtitle: DEFAULT_LOGIN_SUBTITLE_EN,
  authHeadline: DEFAULT_AUTH_HEADLINE_EN,
  authBody: DEFAULT_AUTH_BODY_EN,
  accentColor: DEFAULT_ACCENT_COLOR,
  sidebarBg: DEFAULT_SIDEBAR_BG,
  sidebarText: DEFAULT_SIDEBAR_TEXT,
  sidebarActiveBg: DEFAULT_SIDEBAR_ACTIVE_BG,
  sidebarActiveText: DEFAULT_SIDEBAR_ACTIVE_TEXT,
  sidebarBgLight: DEFAULT_SIDEBAR_BG_LIGHT,
  sidebarTextLight: DEFAULT_SIDEBAR_TEXT_LIGHT,
  sidebarActiveBgLight: DEFAULT_SIDEBAR_ACTIVE_BG_LIGHT,
  sidebarActiveTextLight: DEFAULT_SIDEBAR_ACTIVE_TEXT_LIGHT,
  selectionBg: DEFAULT_SELECTION_BG,
  selectionText: DEFAULT_SELECTION_TEXT,
  textColor: DEFAULT_TEXT_COLOR,
  textColorDark: DEFAULT_TEXT_COLOR_DARK,
  fontFamily: DEFAULT_FONT_FAMILY,
  logoUrl: '',
  faviconUrl: '',
  logoUploaded: false,
  faviconUploaded: false,
  pluginMenuName: DEFAULT_PLUGIN_MENU_NAME,
  pluginPageTitle: DEFAULT_PLUGIN_PAGE_TITLE,
  tablePageSize: 20,
  tablePageSizeOptions: [...DEFAULT_TABLE_PAGE_SIZE_OPTIONS],
  retentionDayChoices: [...DEFAULT_RETENTION_DAYS],
  ready: false,
});

const ADMIN_UI_PATH = '/v1/admin-ui';

export function UiPrefsProvider(props: { children: ReactNode }) {
  const { text } = useAdminLocale();
  const remote = useRemote<{
    productName?: string;
    loginSubtitle?: string;
    authHeadline?: string;
    authBody?: string;
    accentColor?: string;
    sidebarBg?: string;
    sidebarText?: string;
    sidebarActiveBg?: string;
    sidebarActiveText?: string;
    sidebarBgLight?: string;
    sidebarTextLight?: string;
    sidebarActiveBgLight?: string;
    sidebarActiveTextLight?: string;
    selectionBg?: string;
    selectionText?: string;
    textColor?: string;
    textColorDark?: string;
    fontFamily?: string;
    logoUrl?: string;
    faviconUrl?: string;
    logoUploaded?: boolean;
    faviconUploaded?: boolean;
    pluginMenuName?: string;
    pluginPageTitle?: string;
    tablePageSize: number;
    tablePageSizeOptions?: number[];
    retentionDayChoices?: number[];
  }>(ADMIN_UI_PATH, null);

  const tablePageSizeOptions =
    remote.data?.tablePageSizeOptions && remote.data.tablePageSizeOptions.length > 0
      ? remote.data.tablePageSizeOptions
      : [...DEFAULT_TABLE_PAGE_SIZE_OPTIONS];
  const tablePageSize = normalizeTablePageSize(
    remote.data?.tablePageSize ?? 20,
    tablePageSizeOptions,
  );
  const productName = remote.data?.productName?.trim() || DEFAULT_PRODUCT_NAME;
  const localizedDefault = (value: string | undefined, el: string, en: string) => {
    const normalized = value?.trim();
    return !normalized || normalized === el ? text(el, en) : normalized;
  };
  const loginSubtitle = localizedDefault(
    remote.data?.loginSubtitle,
    DEFAULT_LOGIN_SUBTITLE,
    DEFAULT_LOGIN_SUBTITLE_EN,
  );
  const authHeadline = localizedDefault(
    remote.data?.authHeadline,
    DEFAULT_AUTH_HEADLINE,
    DEFAULT_AUTH_HEADLINE_EN,
  );
  const authBody = localizedDefault(remote.data?.authBody, DEFAULT_AUTH_BODY, DEFAULT_AUTH_BODY_EN);
  const accentColor = remote.data?.accentColor?.trim() || DEFAULT_ACCENT_COLOR;
  const sidebarBg = remote.data?.sidebarBg?.trim() || DEFAULT_SIDEBAR_BG;
  const sidebarText = remote.data?.sidebarText?.trim() || DEFAULT_SIDEBAR_TEXT;
  const sidebarActiveBg = remote.data?.sidebarActiveBg?.trim() || DEFAULT_SIDEBAR_ACTIVE_BG;
  const sidebarActiveText = remote.data?.sidebarActiveText?.trim() || DEFAULT_SIDEBAR_ACTIVE_TEXT;
  const sidebarBgLight = remote.data?.sidebarBgLight?.trim() || DEFAULT_SIDEBAR_BG_LIGHT;
  const sidebarTextLight = remote.data?.sidebarTextLight?.trim() || DEFAULT_SIDEBAR_TEXT_LIGHT;
  const sidebarActiveBgLight =
    remote.data?.sidebarActiveBgLight?.trim() || DEFAULT_SIDEBAR_ACTIVE_BG_LIGHT;
  const sidebarActiveTextLight =
    remote.data?.sidebarActiveTextLight?.trim() || DEFAULT_SIDEBAR_ACTIVE_TEXT_LIGHT;
  const selectionBg = remote.data?.selectionBg?.trim() || DEFAULT_SELECTION_BG;
  const selectionText = remote.data?.selectionText?.trim() || DEFAULT_SELECTION_TEXT;
  const textColor = remote.data?.textColor?.trim() || DEFAULT_TEXT_COLOR;
  const textColorDark = remote.data?.textColorDark?.trim() || DEFAULT_TEXT_COLOR_DARK;
  const fontFamily = remote.data?.fontFamily?.trim() || DEFAULT_FONT_FAMILY;
  const logoUrl = resolveBrandUrl(remote.data?.logoUrl?.trim() || '');
  const faviconUrl = resolveBrandUrl(remote.data?.faviconUrl?.trim() || '');
  const logoUploaded = Boolean(remote.data?.logoUploaded);
  const faviconUploaded = Boolean(remote.data?.faviconUploaded);
  const pluginMenuName = remote.data?.pluginMenuName?.trim() || DEFAULT_PLUGIN_MENU_NAME;
  const pluginPageTitle = remote.data?.pluginPageTitle?.trim() || DEFAULT_PLUGIN_PAGE_TITLE;
  const retentionDayChoices =
    remote.data?.retentionDayChoices && remote.data.retentionDayChoices.length > 0
      ? remote.data.retentionDayChoices
      : [...DEFAULT_RETENTION_DAYS];

  useEffect(() => {
    document.title = productName;
  }, [productName]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--admin-accent', accentColor);
    // Theme-specific sidebar prefs (picked up in index.css for :root vs html.dark).
    root.style.setProperty('--admin-sidebar-bg-pref', sidebarBg);
    root.style.setProperty('--admin-sidebar-text-pref', sidebarText);
    root.style.setProperty('--admin-sidebar-active-bg-pref', sidebarActiveBg);
    root.style.setProperty('--admin-sidebar-active-text-pref', sidebarActiveText);
    root.style.setProperty('--admin-sidebar-bg-light-pref', sidebarBgLight);
    root.style.setProperty('--admin-sidebar-text-light-pref', sidebarTextLight);
    root.style.setProperty('--admin-sidebar-active-bg-light-pref', sidebarActiveBgLight);
    root.style.setProperty('--admin-sidebar-active-text-light-pref', sidebarActiveTextLight);
    const lightSidebarColors = readableSidebarColors(
      sidebarBgLight,
      sidebarActiveBgLight,
      sidebarActiveTextLight,
      accentColor,
    );
    const darkSidebarColors = readableSidebarColors(
      sidebarBg,
      sidebarActiveBg,
      sidebarActiveText,
      accentColor,
    );
    root.style.setProperty('--admin-sidebar-active-bg-light-safe', lightSidebarColors.background);
    root.style.setProperty('--admin-sidebar-active-text-light-safe', lightSidebarColors.text);
    root.style.setProperty('--admin-sidebar-active-bg-safe', darkSidebarColors.background);
    root.style.setProperty('--admin-sidebar-active-text-safe', darkSidebarColors.text);
    root.style.setProperty('--admin-selection-bg', selectionBg);
    root.style.setProperty('--admin-selection-text', selectionText);
    root.style.setProperty('--admin-text', textColor);
    root.style.setProperty('--admin-text-dark', textColorDark);
    root.style.setProperty(
      '--font-sans',
      ADMIN_FONT_STACKS[fontFamily] ??
        ADMIN_FONT_STACKS.ibm_plex ??
        "'IBM Plex Sans', 'Segoe UI', ui-sans-serif, system-ui, sans-serif",
    );
    applyFavicon(faviconUrl);
  }, [
    accentColor,
    sidebarBg,
    sidebarText,
    sidebarActiveBg,
    sidebarActiveText,
    sidebarBgLight,
    sidebarTextLight,
    sidebarActiveBgLight,
    sidebarActiveTextLight,
    selectionBg,
    selectionText,
    textColor,
    textColorDark,
    fontFamily,
    faviconUrl,
  ]);

  return (
    <UiPrefsContext.Provider
      value={{
        productName,
        loginSubtitle,
        authHeadline,
        authBody,
        accentColor,
        sidebarBg,
        sidebarText,
        sidebarActiveBg,
        sidebarActiveText,
        sidebarBgLight,
        sidebarTextLight,
        sidebarActiveBgLight,
        sidebarActiveTextLight,
        selectionBg,
        selectionText,
        textColor,
        textColorDark,
        fontFamily,
        logoUrl,
        faviconUrl,
        logoUploaded,
        faviconUploaded,
        pluginMenuName,
        pluginPageTitle,
        tablePageSize,
        tablePageSizeOptions,
        retentionDayChoices,
        ready: Boolean(remote.data) || Boolean(remote.error),
      }}
    >
      {props.children}
    </UiPrefsContext.Provider>
  );
}

export function useUiPrefs(): UiPrefs {
  return useContext(UiPrefsContext);
}

/** Call after platform settings save so shell branding / paging refresh. */
export function refreshAdminUiPrefs(): void {
  void mutate((key) => Array.isArray(key) && key[0] === ADMIN_UI_PATH, undefined, {
    revalidate: true,
  });
}

function resolveBrandUrl(url: string): string {
  if (!url) return '';
  return url.startsWith('/') ? buildApiUrl(url) : url;
}

function applyFavicon(href: string): void {
  const selector = 'link[rel="icon"]';
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}
